/* mookipeels theme script
   Ported from the mookipeels prototype: 3D sticker carousel, product viewer, cart drawer on the
   Shopify AJAX cart, welcome wheel, menu, search, marquees and scroll animations. */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { createStickerKit } from 'mp-sticker3d';

const MP = window.MP || {};
const { gsap, ScrollTrigger } = window;
gsap.registerPlugin(ScrollTrigger);
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TPL = MP.template || document.body.className.replace(/.*tpl-([a-z_]+).*/, '$1');
const R = MP.routes || {};
const C = { leaf: '#6CA24A', sun: '#F4C542', mari: '#F28C38', blush: '#F7B7C4', cream: '#FFF7E6', sage: '#C7DDAE' };

function money(cents) {
  const fmt = MP.moneyFormat || 'S${{amount}}';
  const n = (cents || 0) / 100;
  const two = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => k === 'amount_no_decimals' ? Math.round(n).toString() : k === 'amount_with_comma_separator' ? n.toFixed(2).replace('.', ',') : k === 'amount_no_decimals_with_comma_separator' ? Math.round(n).toString() : two);
}
const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { } }
};

// === CATALOGUE (from the shop collection) ===
const STICKERS = (MP.products || []).map((p, i) => ({ ...p, no: i + 1, name: p.title }));
const byHandle = Object.fromEntries(STICKERS.map(s => [s.handle, s]));
const BUNDLE = MP.bundle || { enabled: false, size: 3, price: 650, handles: [] };
const UNIT = STICKERS[0] ? STICKERS[0].price : 250;
const SET_SAVE = Math.max(0, UNIT * BUNDLE.size - BUNDLE.price);
const plusSVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
const flowerSVG = (cls = 'flower') => `<svg class="${cls}" viewBox="-12 -12 24 24" aria-hidden="true"><use href="#i-flower"/><use href="#i-flower-dot"/></svg>`;

let toastT;
function toast(html) { const t = $('#toast'); if (!t) return; t.innerHTML = html; t.classList.add('on'); clearTimeout(toastT); toastT = setTimeout(() => t.classList.remove('on'), 2800); }

// === SMOOTH SCROLL ===
let lenis = null;
if (!reduce && window.Lenis) {
  lenis = new window.Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 0.9 });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(t => lenis.raf(t * 1000)); gsap.ticker.lagSmoothing(0);
}
const scrollTo = (y, opts = {}) => lenis ? lenis.scrollTo(y, opts) : window.scrollTo({ top: typeof y === 'number' ? y : (y.getBoundingClientRect().top + scrollY + (opts.offset || -70)), behavior: opts.immediate ? 'auto' : 'smooth' });
const scrollVel = () => lenis ? lenis.velocity : 0;
let locks = 0;
const lockScroll = (on) => { locks = Math.max(0, locks + (on ? 1 : -1)); const l = locks > 0; if (lenis) l ? lenis.stop() : lenis.start(); document.documentElement.style.overflow = l ? 'hidden' : ''; };
let menuOpen = false;
const paused = () => menuOpen || searchOpen || !!drawerMode || (document.getElementById('wheel') && !document.getElementById('wheel').hidden);

// === MARQUEES: speed reacts to scroll velocity ===
const marquees = [];
$$('[data-marquee]').forEach(el => {
  const track = el.classList.contains('track') ? el : $('.track', el); if (!track || !track.children[0]) return;
  const first = track.children[0].outerHTML;
  track.insertAdjacentHTML('beforeend', first);
  let guard = 0; while (track.scrollWidth < innerWidth * 2.2 && guard++ < 14) track.insertAdjacentHTML('beforeend', first);
  marquees.push({ track, x: 0, speed: +(el.dataset.speed || 40), dir: +(el.dataset.dir || -1), boost: 0 });
});
gsap.ticker.add((t, dt) => {
  const v = scrollVel();
  marquees.forEach(m => {
    m.boost = lerp(m.boost, Math.min(10, Math.abs(v) * 0.6), 0.08);
    const d = v < -0.5 ? -1 : 1;
    m.x += m.dir * d * (m.speed + m.boost * 30) * dt / 1000;
    const w = m.track.children[0].offsetWidth || 1;
    if (m.x <= -w) m.x += w; if (m.x > 0) m.x -= w;
    m.track.style.transform = `translate3d(${m.x}px,0,0)`;
  });
});

// === SCALLOPED EDGES ===
function scallopPath(w, h, n, over = 4) {
  const step = w / n; let d = `M0 ${-over} L0 ${h * 0.55}`;
  for (let i = 0; i < n; i++) d += ` Q${i * step + step / 2} ${-h * 0.35} ${(i + 1) * step} ${h * 0.55}`;
  return d + ` L${w} ${-over} Z`;
}
const fitScallops = () => { const p = $('#scallopPath'); if (p) p.setAttribute('d', scallopPath(1200, 28, Math.max(9, Math.round(innerWidth / 34)))); };
fitScallops(); addEventListener('resize', fitScallops);

// === ROLL TEXT ===
$$('.roll').forEach(r => { const t = r.dataset.roll || r.textContent; r.innerHTML = [...t].map(c => `<i>${c === ' ' ? '&nbsp;' : esc(c)}</i>`).join(''); $$('i', r).forEach((i, k) => i.style.transitionDelay = (k * 0.018) + 's'); });

// === DROPDOWN: styled listbox driving a hidden native <select> ===
const chevSVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
const tickSVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
function fancySelect(sel) {
  if (!sel || sel._fsync || !sel.options.length) return;
  const wrap = document.createElement('div'); wrap.className = 'fsel';
  sel.parentNode.insertBefore(wrap, sel); wrap.appendChild(sel);
  sel.classList.add('fsel__native'); sel.tabIndex = -1; sel.setAttribute('aria-hidden', 'true');
  const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'fsel__btn'; btn.id = sel.id + 'Btn';
  btn.setAttribute('aria-haspopup', 'listbox'); btn.setAttribute('aria-expanded', 'false');
  const list = document.createElement('ul'); list.className = 'fsel__list'; list.id = sel.id + 'List'; list.setAttribute('role', 'listbox'); list.tabIndex = -1; list.hidden = true;
  btn.setAttribute('aria-controls', list.id);
  const opts = [...sel.options];
  list.innerHTML = opts.map((o, i) => `<li role="option" id="${sel.id}Opt${i}" data-i="${i}">${esc(o.textContent)}${tickSVG}</li>`).join('');
  wrap.append(btn, list);
  const lab = document.querySelector(`label[for="${sel.id}"]`); if (lab) lab.htmlFor = btn.id;
  if (lab && lab.firstChild && lab.firstChild.nodeType === 3) list.setAttribute('aria-label', lab.firstChild.textContent.trim());
  let active = sel.selectedIndex;
  const items = () => [...list.children];
  function sync() { btn.innerHTML = `<span>${esc(sel.options[sel.selectedIndex].textContent)}</span>${chevSVG}`; items().forEach((li, i) => li.setAttribute('aria-selected', i === sel.selectedIndex)); }
  function mark(i) { active = (i + opts.length) % opts.length; items().forEach((li, k) => li.classList.toggle('is-active', k === active)); list.setAttribute('aria-activedescendant', `${sel.id}Opt${active}`); }
  function open() { if (!list.hidden) return; list.hidden = false; wrap.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); mark(sel.selectedIndex); list.focus(); if (!reduce) gsap.fromTo(list, { opacity: 0, y: -6, scale: .96 }, { opacity: 1, y: 0, scale: 1, duration: .28, ease: 'back.out(2)', clearProps: 'transform' }); }
  function close(focusBtn = true) { if (list.hidden) return; list.hidden = true; wrap.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); if (focusBtn) btn.focus(); }
  function choose(i) { if (sel.selectedIndex !== i) { sel.selectedIndex = i; sel.dispatchEvent(new Event('change', { bubbles: true })); } sync(); close(); }
  btn.addEventListener('click', (e) => { e.preventDefault(); list.hidden ? open() : close(); });
  btn.addEventListener('keydown', (e) => { if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); open(); } });
  list.addEventListener('click', (e) => { const li = e.target.closest('li'); if (li) choose(+li.dataset.i); });
  list.addEventListener('pointermove', (e) => { const li = e.target.closest('li'); if (li) mark(+li.dataset.i); });
  list.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); mark(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); mark(active - 1); }
    else if (e.key === 'Home') { e.preventDefault(); mark(0); }
    else if (e.key === 'End') { e.preventDefault(); mark(opts.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'Tab') close(false);
  });
  document.addEventListener('pointerdown', (e) => { if (!wrap.contains(e.target)) close(false); });
  sel._fsync = sync; sync();
}

// === CART (Shopify AJAX API) ===
let cart = (() => { try { return JSON.parse($('#cartInitial').textContent); } catch (e) { return { items: [], item_count: 0, total_price: 0 }; } })();
let reward = store.get('mp-reward', null); // { code, label, title, email }
const isBundleItem = (it) => BUNDLE.enabled && (!BUNDLE.handles || !BUNDLE.handles.length || BUNDLE.handles.includes(it.handle));
const bundleCount = () => cart.items.reduce((a, it) => a + (isBundleItem(it) ? it.quantity : 0), 0);
function nudgeInfo() {
  const n = bundleCount(), size = BUNDLE.size;
  const rem = n % size, need = rem === 0 ? size : size - rem, sets = Math.floor(n / size);
  return { n, rem, need, sets, filled: rem === 0 && n > 0 ? size : rem, saving: sets * SET_SAVE };
}
async function api(url, body) {
  const res = await fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
  return data;
}
async function refreshCart() { cart = await api(R.cart + '.js'); cartChanged(); return cart; }
function cartChanged() {
  const n = cart.item_count; const b = $('#bagCount'); if (b) { b.textContent = n; b.classList.toggle('has', n > 0); }
  if (drawerMode === 'bag') renderBag();
  refreshSetCards();
}
let adding = false;
async function addToCart(id, qty = 1, quiet = false) {
  if (!id || adding) return false; adding = true;
  try {
    await api(R.cartAdd + '.js', { items: [{ id: +id, quantity: qty }] });
    await refreshCart();
    const line = cart.items.find(it => it.variant_id === +id);
    if (!quiet) {
      const img = line && line.image ? `<img src="${esc(line.image)}" alt="">` : '';
      if (line && isBundleItem(line)) { const ni = nudgeInfo(); toast(img + (ni.rem === 0 ? `Set of ${BUNDLE.size} complete: ${BUNDLE.size} for ${money(BUNDLE.price)} applied` : `Added. ${ni.need} more to make it ${BUNDLE.size} for ${money(BUNDLE.price)}`)); }
      else toast(img + 'Added to your cart');
    }
    gsap.fromTo('#bagBtn', { rotate: -14, scale: 1.2 }, { rotate: 0, scale: 1, duration: .7, ease: 'elastic.out(1,.4)' });
    return true;
  } catch (e) { toast(esc(e.message)); return false; }
  finally { adding = false; }
}
async function changeLine(key, quantity) {
  try { cart = await api(R.cartChange + '.js', { id: key, quantity }); cartChanged(); }
  catch (e) { toast(esc(e.message)); refreshCart(); }
}
async function applyCode(code) {
  if (!code) return;
  try { await api(R.cartUpdate + '.js', { discount: code }); } catch (e) { }
  try { await fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(R.cart + '.js')}`, { credentials: 'same-origin' }); } catch (e) { }
  refreshCart().catch(() => { });
}
document.addEventListener('click', e => {
  const add = e.target.closest('[data-add]'); if (!add) return;
  e.preventDefault(); e.stopPropagation();
  if (adding || add.classList.contains('is-busy')) return;
  const quick = add.classList.contains('quick-add');
  if (quick) { clearTimeout(add._t); add.classList.remove('is-done', 'is-err'); add.classList.add('is-busy'); add.setAttribute('aria-busy', 'true'); }
  addToCart(add.dataset.add).then(ok => {
    if (!quick) return;
    add.classList.remove('is-busy'); add.removeAttribute('aria-busy');
    if (ok) { add.innerHTML = tickSVG; add.classList.add('is-done'); add._t = setTimeout(() => { add.classList.remove('is-done'); add.innerHTML = plusSVG; }, 1500); }
    else { void add.offsetWidth; add.classList.add('is-err'); }
  });
}, true);

// === CARDS ===
function cardHTML(p) {
  return `<a class="card" href="${esc(p.url)}" style="--tint:${esc(p.tint)}" data-cursor="peel">
    <div class="card__media">${p.available ? '' : '<span class="card__badge">sold out</span>'}<div class="card__sticker"><img src="${esc(p.art)}" alt="${esc(p.name)} sticker" loading="lazy"></div><img class="card__wild" src="${esc(p.scene)}" alt="" loading="lazy">
      ${p.available ? `<button class="quick-add" type="button" data-add="${p.id}" aria-label="Add ${esc(p.name)} to cart">${plusSVG}</button>` : ''}
    </div>
    <div class="card__row"><div style="min-width:0"><div class="card__name">${esc(p.name)}</div>${p.sub ? `<div class="card__sub">${esc(p.sub)}</div>` : ''}</div><div class="card__price">${money(p.price)}${BUNDLE.enabled ? `<small>${BUNDLE.size} for ${money(BUNDLE.price)}</small>` : ''}</div></div>
  </a>`;
}
function bindCards(root = document) {
  $$('.card', root).forEach(card => {
    if (card._bound) return; card._bound = true;
    const st = $('.card__sticker', card); if (!st || !finePointer) return;
    card.addEventListener('pointermove', e => { const r = card.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width - .5, y = (e.clientY - r.top) / r.height - .5; st.style.transform = `rotateY(${x * 18}deg) rotateX(${-y * 14}deg)`; });
    card.addEventListener('pointerleave', () => st.style.transform = '');
  });
}
bindCards();

// Accordions that keep one item open (and swap a matching picture)
$$('.acc[data-single]').forEach(acc => {
  const ds = $$(':scope > details', acc); const media = acc.closest('.feature__grid') ? $$('.feature__media img', acc.closest('.feature__grid')) : [];
  ds.forEach(d => d.addEventListener('toggle', () => { if (!d.open) return; ds.forEach(o => { if (o !== d) o.open = false; }); media.forEach((im, i) => im.classList.toggle('on', i === +d.dataset.i)); }));
});

// Draggable stickers around the wordmark
(function dragStickers() {
  const host = $('#wordmark'); if (!host) return;
  const srcs = $$('#dragSources img').map(i => i.dataset.src);
  const spots = [[6, 'top', -14], [47, 'top', -6, .58], [78, 'top', 10], [22, 'bottom', 6], [64, 'bottom', -8]];
  srcs.slice(0, 5).forEach((src, i) => {
    const [x, edge, r, k = 1] = spots[i];
    const im = document.createElement('img'); im.src = src; im.alt = ''; im.className = 'drag'; im.draggable = false;
    if (k !== 1) im.style.width = `calc(clamp(84px, 12.5vw, 190px) * ${k})`;
    im.style.left = x + '%'; im.style[edge] = '12px'; im.style.rotate = r + 'deg'; im.dataset.cursor = 'drag'; host.appendChild(im);
    let sx, sy, ox, oy;
    im.addEventListener('pointerdown', e => {
      e.preventDefault(); im.setPointerCapture(e.pointerId); im.classList.add('is-dragging'); host.appendChild(im);
      const hr = host.getBoundingClientRect(); sx = e.clientX; sy = e.clientY; ox = im.offsetLeft; oy = im.offsetTop; im.style.top = oy + 'px'; im.style.bottom = 'auto';
      const move = ev => { const nx = clamp(ox + ev.clientX - sx, -20, hr.width - im.offsetWidth + 20), ny = clamp(oy + ev.clientY - sy, -10, hr.height - im.offsetHeight + 10); im.style.left = nx + 'px'; im.style.top = ny + 'px'; im.style.rotate = clamp((ev.clientX - sx) * 0.08, -20, 20) + 'deg'; };
      const up = () => { im.classList.remove('is-dragging'); im.removeEventListener('pointermove', move); im.removeEventListener('pointerup', up); im.removeEventListener('pointercancel', up); gsap.fromTo(im, { scale: 1.08 }, { scale: 1, duration: .5, ease: 'back.out(3)' }); };
      im.addEventListener('pointermove', move); im.addEventListener('pointerup', up); im.addEventListener('pointercancel', up);
    });
  });
})();

// Photo strip: drag to scroll with a mouse
$$('.wild__strip').forEach(el => {
  let down = false, sx = 0, sl = 0, moved = 0;
  el.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse') return; down = true; moved = 0; sx = e.clientX; sl = el.scrollLeft; });
  addEventListener('pointermove', e => { if (!down) return; const dx = e.clientX - sx; moved = Math.max(moved, Math.abs(dx)); if (moved > 4) el.classList.add('is-drag'); el.scrollLeft = sl - dx; });
  addEventListener('pointerup', () => { down = false; setTimeout(() => el.classList.remove('is-drag'), 0); });
  el.addEventListener('click', e => { if (moved > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
});

// === THREE: shared setup ===
const kit = createStickerKit(THREE);
const IMGS = {};
const loadImg = (src) => new Promise((res) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.decoding = 'async'; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
const fallbackTex = () => { const c = document.createElement('canvas'); c.width = c.height = 64; const x = c.getContext('2d'); x.fillStyle = '#fff'; x.beginPath(); x.arc(32, 32, 30, 0, 7); x.fill(); return c; };
function envFor(renderer) { const pm = new THREE.PMREMGenerator(renderer); const t = pm.fromScene(new RoomEnvironment(), 0.04).texture; pm.dispose(); return t; }
function lights(scene) {
  scene.add(new THREE.HemisphereLight('#fffaf0', '#d9c9a8', 1.0));
  const key = new THREE.DirectionalLight('#fff4e0', 2.3); key.position.set(-3, 5, 6); scene.add(key);
  const rim = new THREE.PointLight('#ffe2b8', 22, 40); rim.position.set(5, 2, -3); scene.add(rim);
  const fill = new THREE.DirectionalLight('#e9f3df', 0.7); fill.position.set(4, -2, 5); scene.add(fill);
}
const hitAlpha = (() => {
  const cache = new Map();
  return (img, uv) => {
    let d = cache.get(img);
    if (!d) { const c = document.createElement('canvas'); c.width = 96; c.height = Math.round(96 * img.height / img.width) || 96; const x = c.getContext('2d'); x.drawImage(img, 0, 0, c.width, c.height); try { d = { w: c.width, h: c.height, a: x.getImageData(0, 0, c.width, c.height).data }; } catch (e) { d = { w: 1, h: 1, a: [0, 0, 0, 255] }; } cache.set(img, d); }
    const px = Math.floor(clamp(uv.x, 0, .999) * d.w), py = Math.floor(clamp(1 - uv.y, 0, .999) * d.h);
    return d.a[(py * d.w + px) * 4 + 3] > 100;
  };
})();

// === CUSTOM CURSOR ===
const Cursor = (() => {
  const el = $('#cursor'); if (!el || !finePointer) return { label() { } };
  const span = $('span', el); let x = innerWidth / 2, y = innerHeight / 2, cx = x, cy = y, forced = null;
  addEventListener('pointermove', e => { if (e.pointerType !== 'mouse') return; el.style.opacity = 1; x = e.clientX; y = e.clientY; if (forced === null) { const t = e.target.closest && e.target.closest('[data-cursor]'); set(t ? t.dataset.cursor : null); } }, { passive: true });
  function set(l) { el.classList.toggle('big', !!l); if (l) span.textContent = l; }
  gsap.ticker.add(() => { cx = lerp(cx, x, .22); cy = lerp(cy, y, .22); el.style.transform = `translate3d(${cx}px,${cy}px,0)`; });
  return { label(l) { forced = l; set(l); if (l === null) forced = null; } };
})();

// === HERO: sticker wheel on a scalloped platter, then the "Big moods" finale ===
const Hero = (() => {
  const hero = $('#hero'); if (!hero || !STICKERS.length) { if (hero) hero.hidden = true; return null; }
  const N = STICKERS.length, STEP = Math.PI * 2 / N, CAROUSEL = 0.78;
  const stage = $('#heroStage'), canvas = $('#heroCanvas'), disc = $('#disc');
  let renderer, scene, camera, items = [], cfg = {}, alive = false;
  let st = null, snapT, dirDown = true, lastMetaT = 0;
  let prog = 0, idxT = 0, idx = 0, morph = 0, morphT = 0, shown = -1, hovered = -1; const introT = { v: 0 };
  const mouse = new THREE.Vector2(), sm = { x: 0, y: 0 }, ray = new THREE.Raycaster();
  const BACK = gsap.parseEase('back.out(2)');
  const SD = [[-.78, .52, -.3], [.8, .56, .2], [-.86, -.5, .3], [.84, -.52, -.25], [0, -.9, .1], [-.3, .9, .1], [.32, -.9, -.1], [.1, .95, .2]];
  const SM = [[-.62, .8, -.2], [.62, .84, .2], [-.64, -.8, .3], [.64, -.82, -.25], [0, -.96, .1], [-.2, .96, .1], [.3, -.95, -.1], [.1, .9, .2]];
  function build() {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
    renderer.toneMapping = THREE.NeutralToneMapping; renderer.toneMappingExposure = 1.02;
    scene = new THREE.Scene(); scene.environment = envFor(renderer); scene.environmentIntensity = 0.45; lights(scene);
    camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100); camera.position.set(0, 0, 14);
    STICKERS.forEach((s) => { const it = kit.make(IMGS[s.handle] || fallbackTex(), { size: 1, shadow: true, renderer, finish: 'matte' }); it.peel = 0; scene.add(it.group); items.push(it); });
    layout(); alive = true;
  }
  function svgDisc() {
    const n = 26, R0 = 100, bump = 7; let d = '';
    for (let i = 0; i <= n * 8; i++) { const a = i / (n * 8) * Math.PI * 2; const r = R0 + bump * Math.pow(Math.abs(Math.cos(a * n / 2)), 0.7); d += (i ? 'L' : 'M') + (Math.cos(a) * r).toFixed(2) + ' ' + (Math.sin(a) * r).toFixed(2); }
    const txt = esc((hero.dataset.discText || 'MOOKIPEELS ✿').trim() + ' ');
    return `<svg viewBox="-110 -110 220 220" aria-hidden="true"><path d="${d}Z" fill="${C.sage}" stroke="#2B2219" stroke-width=".9"/><path id="discFill" d="${d}Z" fill="${C.leaf}" opacity="0"/><g class="disc-deco"><circle r="88" fill="none" stroke="${C.leaf}" stroke-width="1.1" stroke-dasharray="2.4 2.4"/><defs><path id="discText" d="M0 0m-80 0a80 80 0 1 1 160 0a80 80 0 1 1-160 0"/></defs><text font-family="Nunito, sans-serif" font-weight="900" font-size="4.2" letter-spacing="1.6" fill="#4C7A30"><textPath href="#discText">${txt.repeat(2)}</textPath></text></g></svg>`;
  }
  disc.innerHTML = svgDisc();
  function layout() {
    const w = stage.clientWidth, h = stage.clientHeight; if (!w || !renderer) return;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    const mobile = w < 700;
    const halfH = camera.position.z * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    const ppu = h / (2 * halfH), sr = stage.getBoundingClientRect();
    const headB = $('.hero__head').getBoundingClientRect().bottom - sr.top, ctaT = $('.hero__cta').getBoundingClientRect().top - sr.top - (mobile ? 64 : 10);
    const avail = Math.max(1.2, (ctaT - headB) / ppu);
    const S = Math.min(mobile ? Math.min(2.7, halfH * camera.aspect * 1.3) : (w < 1100 ? 2.8 : 3.2), avail * 0.9);
    const cy = halfH - (headB + (ctaT - headB) * 0.5) / ppu;
    cfg = { mobile, halfH, halfW: halfH * camera.aspect, S, R: mobile ? 4.6 : 7.2, cy, ppu };
    const discR = (cfg.R - cfg.S * 0.08) * ppu * 1.1;
    disc.style.width = disc.style.height = (discR * 2) + 'px';
    disc.style.marginLeft = disc.style.marginTop = (-discR) + 'px';
    disc.style.top = (h / 2 - (cfg.cy - cfg.R) * ppu) + 'px';
    const far = Math.hypot(w / 2, Math.max(h - parseFloat(disc.style.top), parseFloat(disc.style.top)));
    cfg.coverScale = far / discR * 1.08;
  }
  function setProgress(p) { prog = p; idxT = clamp(p / CAROUSEL) * (N - 1); morphT = clamp((p - CAROUSEL) / (1 - CAROUSEL)); }
  const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  function frame(time, dt) {
    const visible = !!st && scrollY < st.end + innerHeight * 1.05;
    if (!alive || !visible || document.hidden || paused()) return;
    const k60 = (a) => 1 - Math.pow(1 - a, Math.min(6, (dt || 16.7) / 16.7));
    idx = lerp(idx, idxT, reduce ? 1 : k60(0.11)); morph = lerp(morph, morphT, reduce ? 1 : k60(0.12));
    sm.x = lerp(sm.x, mouse.x, k60(.06)); sm.y = lerp(sm.y, mouse.y, k60(.06));
    const e = ease(clamp(morph)), wheel = -idx * STEP, vel = Math.abs(scrollVel());
    items.forEach((it, i) => {
      let th = i * STEP + wheel; th = Math.atan2(Math.sin(th), Math.cos(th));
      const f = Math.pow(Math.max(0, Math.cos(th)), 5);
      const pop = clamp(introT.v * 1.6 - Math.abs(th) * 0.35);
      const wx = Math.sin(th) * cfg.R, wy = cfg.cy - cfg.R + Math.cos(th) * cfg.R, wz = f * 1.1;
      const sc = (cfg.mobile ? SM : SD)[i % 8], sx = sc[0] * cfg.halfW * (cfg.mobile ? .8 : .86), sy = sc[1] * cfg.halfH * .8 + Math.sin(time * 1.1 + i * 1.7) * .12, sz = sc[2];
      const g = it.group;
      g.position.set(lerp(wx, sx, e), lerp(wy, sy, e), lerp(wz, sz, e));
      g.scale.setScalar(cfg.S * lerp(0.6 + 0.4 * f, cfg.mobile ? 0.36 : 0.5, e) * (0.0001 + (reduce ? 1 : BACK(pop))));
      g.rotation.z = lerp(-th * 0.85, (sc[0] * 0.5 - sc[1] * 0.3), e) + Math.sin(time * 0.9 + i) * 0.035;
      g.rotation.y = sm.x * 0.35 * (f + e) + (1 - pop) * 1.2;
      g.rotation.x = -sm.y * 0.25 * (f + e);
      g.visible = Math.cos(th) > -0.3 || e > 0.02;
      it.peel = lerp(it.peel, (i === hovered && e < 0.05) ? it.diag * 0.24 : 0, k60(0.12));
      it.U.uPeel.value = it.peel; it.U.uTime.value = time;
      it.U.uWave.value = 0.18 + Math.min(1.3, vel * 0.035) + e * 0.5;
      if (it.shadowMesh) it.shadowMesh.material.opacity = 0.26 * (1 - e * 0.6);
    });
    disc.style.transform = `rotate(${-wheel * 180 / Math.PI}deg) scale(${lerp(1, cfg.coverScale || 3, e)})`;
    const fill = disc.querySelector('#discFill'); if (fill) fill.setAttribute('opacity', clamp(e * 1.6).toFixed(3));
    const deco = disc.querySelector('.disc-deco'); if (deco) deco.setAttribute('opacity', clamp(1 - e * 2).toFixed(3));
    const ui = $('#heroUI'); ui.style.opacity = clamp(1 - e * 3).toFixed(3); ui.style.visibility = e > .33 ? 'hidden' : 'visible';
    const fin = $('#finale'); fin.style.opacity = clamp((e - .45) * 2.2).toFixed(3); fin.style.transform = `translateY(${(1 - clamp((e - .4) * 1.8)) * 40}px)`; fin.classList.toggle('on', e > .7);
    const k = Math.round(clamp(idx, 0, N - 1)); if (k !== shown) showMeta(k);
    renderer.render(scene, camera);
  }
  function showMeta(k) {
    const first = shown < 0; shown = k; const s = STICKERS[k];
    $('#heroSub').innerHTML = `${s.sub ? esc(s.sub) + ' · ' : ''}<b>${money(s.price)}</b>${BUNDLE.enabled ? ` · <span class="deal-chip">${BUNDLE.size} for ${money(BUNDLE.price)}</span>` : ''}`;
    $('#heroCount').textContent = `${String(k + 1).padStart(2, '0')} / ${String(N).padStart(2, '0')}`;
    $('#heroView').href = s.url;
    const add = $('#heroAdd'); add.disabled = !s.available; add.textContent = s.available ? 'Add to cart' : 'Sold out';
    const title = $('#heroTitle'); title.style.setProperty('--len', Math.max(9, s.name.length)); title.innerHTML = [...s.name].map(c => `<span class="ch">${c === ' ' ? '&nbsp;' : esc(c)}</span>`).join('');
    const now = performance.now(), calm = now - lastMetaT > 260; lastMetaT = now;
    if (!first && !reduce && calm) gsap.from($$('.ch', title), { yPercent: 115, rotate: 10, duration: .7, stagger: .022, ease: 'back.out(1.7)' });
  }
  function pick(e) {
    const r = canvas.getBoundingClientRect(); mouse.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    if (!alive || morph > .05) { hovered = -1; return -1; }
    ray.setFromCamera(mouse, camera);
    const hits = ray.intersectObjects(items.filter(it => it.group.visible).map(it => it.group.children[0]), false);
    for (const h of hits) { const i = items.findIndex(it => it.group.children[0] === h.object); const im = IMGS[STICKERS[i].handle]; if (!im || hitAlpha(im, h.uv)) return i; }
    return -1;
  }
  canvas.addEventListener('pointermove', e => { hovered = pick(e); canvas.style.cursor = hovered >= 0 ? 'pointer' : ''; Cursor.label(hovered >= 0 ? 'peel' : null); });
  canvas.addEventListener('pointerleave', () => { hovered = -1; Cursor.label(null); mouse.set(0, 0); });
  canvas.addEventListener('click', e => { const i = pick(e); if (i >= 0) { if (i === Math.round(idx)) navigate(STICKERS[i].url); else goTo(i); } });
  $('#heroAdd').addEventListener('click', () => { const s = STICKERS[Math.round(clamp(idx, 0, N - 1))]; if (s.available) addToCart(s.id); });
  $('#heroPrev').addEventListener('click', () => goTo(Math.round(idx) - 1));
  $('#heroNext').addEventListener('click', () => goTo(Math.round(idx) + 1));
  function goTo(i) { if (!st) return; i = clamp(i, 0, N - 1); scrollTo(st.start + (i / Math.max(1, N - 1)) * CAROUSEL * (st.end - st.start), { duration: 0.9 }); }
  function snap() {
    if (!st || !st.isActive) return;
    if (prog > CAROUSEL + 0.002) { if (prog < 0.999) scrollTo(dirDown && morphT > 0.12 ? st.end + 1 : st.start + CAROUSEL * (st.end - st.start), { duration: .9 }); return; }
    const k = Math.round(idxT), want = st.start + (k / Math.max(1, N - 1)) * CAROUSEL * (st.end - st.start);
    if (Math.abs(scrollY - want) > 3) scrollTo(want, { duration: 0.55 });
  }
  function mount() {
    if (!renderer) build(); layout();
    st = ScrollTrigger.create({ trigger: '#hero', start: 'top top', end: () => '+=' + Math.round(innerHeight * (0.42 * (N - 1) + 1.2)), pin: true, onUpdate: s => setProgress(s.progress) });
    let lastY = scrollY;
    const onScroll = () => { const y = scrollY; if (Math.abs(y - lastY) > 0.5) dirDown = y > lastY; lastY = y; clearTimeout(snapT); snapT = setTimeout(snap, 160); };
    if (lenis) lenis.on('scroll', onScroll); else addEventListener('scroll', onScroll, { passive: true });
  }
  function intro() { if (reduce) { introT.v = 1; return; } gsap.to(introT, { v: 1, duration: 1.8, ease: 'power2.out' }); gsap.from('#heroTitle .ch', { yPercent: 120, rotate: 12, duration: 1, stagger: .03, ease: 'back.out(1.6)', delay: .2 }); gsap.from('.hero__sub, .hero__cta, .hero__nav, .scrollbadge', { y: 30, opacity: 0, duration: .9, stagger: .07, ease: 'power3.out', delay: .35 }); gsap.from(disc, { yPercent: 18, duration: 1.4, ease: 'power3.out' }); }
  gsap.ticker.add(frame);
  addEventListener('resize', () => layout());
  addEventListener('keydown', e => { if (!menuOpen && !drawerMode && scrollY < innerHeight * 4 && (e.key === 'ArrowRight' || e.key === 'ArrowLeft') && document.activeElement === document.body) (e.key === 'ArrowRight' ? $('#heroNext') : $('#heroPrev')).click(); });
  return { mount, intro, layout, showMeta, get end() { return st ? st.end : 0; } };
})();

// === 3D VIEWER: drag-to-spin sticker (product page, sticker spotlight) ===
function createViewer() {
  let renderer, scene, camera, root, items = [], canvas, host, alive = false, peelOn = false, peelV = 0, inView = true;
  let rotY = 0, rotX = 0, velY = 0.004, velX = 0, dragging = false, lastX = 0, lastY = 0, idle = 0;
  function mount(el, img, title) {
    host = el; canvas = document.createElement('canvas'); canvas.setAttribute('aria-label', `3D view of the ${title} sticker. Drag to spin.`); canvas.setAttribute('role', 'img');
    host.prepend(canvas);
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2)); renderer.toneMapping = THREE.NeutralToneMapping; renderer.toneMappingExposure = 1.02;
    scene = new THREE.Scene(); scene.environment = envFor(renderer); scene.environmentIntensity = 0.6; lights(scene);
    camera = new THREE.PerspectiveCamera(30, 1, 0.1, 50); camera.position.set(0, 0, 9);
    root = new THREE.Group(); scene.add(root);
    const st = kit.make(img, { size: 3.2, renderer, finish: 'matte', shadow: true, segments: 48 });
    if (st.shadowMesh) st.shadowMesh.position.z = -0.6;
    root.add(st.group); items.push(st);
    size(); alive = true;
    canvas.addEventListener('pointerdown', e => { dragging = true; lastX = e.clientX; lastY = e.clientY; canvas.setPointerCapture(e.pointerId); canvas.classList.add('grabbing'); });
    canvas.addEventListener('pointermove', e => { if (!dragging) return; const dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY; velY = dx * 0.008; velX = dy * 0.005; rotY += velY; rotX = clamp(rotX + velX, -0.7, 0.7); idle = 0; });
    const up = () => { dragging = false; canvas.classList.remove('grabbing'); };
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('dblclick', () => togglePeel());
    if (finePointer) { canvas.addEventListener('pointerenter', () => Cursor.label('drag')); canvas.addEventListener('pointerleave', () => Cursor.label(null)); }
    new IntersectionObserver(([en]) => { inView = en.isIntersecting; }).observe(host);
    if (!reduce) gsap.from(root.scale, { x: .01, y: .01, z: .01, duration: 1.1, ease: 'back.out(1.8)', delay: .2 });
    gsap.ticker.add(frame); addEventListener('resize', size);
  }
  function size() { if (!renderer) return; const r = host.getBoundingClientRect(); renderer.setSize(r.width, r.height, false); camera.aspect = r.width / r.height || 1; camera.updateProjectionMatrix(); }
  function frame(time) {
    if (!alive || document.hidden || paused() || !inView) return;
    if (!dragging) { idle += 1; velY = lerp(velY, idle > 90 ? 0.006 : 0, 0.02); velX *= 0.92; rotY += velY; rotX = lerp(rotX, Math.sin(time * 0.5) * 0.12, 0.02); }
    root.rotation.set(rotX, rotY, 0);
    peelV = lerp(peelV, peelOn ? 1 : 0, 0.07);
    items.forEach((it, i) => { it.U.uPeel.value = peelV * it.diag * 0.42; it.U.uTime.value = time + i; it.U.uWave.value = 0.12; });
    renderer.render(scene, camera);
  }
  function togglePeel(force) { peelOn = typeof force === 'boolean' ? force : !peelOn; const b = host && $('#peelBtn', host); if (b) { b.setAttribute('aria-pressed', peelOn); b.textContent = peelOn ? 'Stick it back' : 'Peel it'; } return peelOn; }
  function show(on) { if (canvas) canvas.style.visibility = on ? 'visible' : 'hidden'; alive = !!renderer && on; }
  return { mount, togglePeel, size, show };
}

// === SET-OF-3 CARD (product page) ===
function setCardHTML(currentHandle) {
  const ni = nudgeInfo(); const sz = BUNDLE.size;
  const dots = Array.from({ length: sz }, (_, i) => `<i class="${i < ni.filled ? 'on' : ''}"></i>`).join('');
  const head = ni.n === 0 ? `Mix any ${sz} for ${money(BUNDLE.price)}` : ni.rem === 0 ? `${ni.sets} set${ni.sets > 1 ? 's' : ''} of ${sz} in your cart` : `${ni.need} more to make it ${sz} for ${money(BUNDLE.price)}`;
  const body = ni.n === 0 ? `Add ${sz} stickers in any mix and every set of ${sz} comes to ${money(BUNDLE.price)}. It applies in your cart automatically.` : ni.rem === 0 ? `You're saving ${money(ni.saving)}. Add ${sz} more for another set at ${money(BUNDLE.price)}.` : `You have ${ni.n} in your cart. Add ${ni.need} more and this set drops to ${money(BUNDLE.price)}.`;
  const others = STICKERS.filter(s => s.handle !== currentHandle && s.available);
  return `<div class="set-card__top"><b>${head}</b><span class="dots" aria-label="${ni.filled} of ${sz} in this set">${dots}</span></div><p>${body}</p><div class="mini-adds">${others.map(s => `<button class="mini-add" type="button" data-add="${s.id}" aria-label="Add ${esc(s.name)} to cart"><img src="${esc(s.art)}" alt="">${esc(s.name)} ${plusSVG}</button>`).join('')}</div>`;
}
function refreshSetCards() { const sc = $('#setCard'); if (sc && BUNDLE.enabled) sc.innerHTML = setCardHTML(sc.dataset.handle); }

// === PRODUCT PAGE ===
async function productPage() {
  const stageEl = $('#stage'); if (!stageEl) return;
  const view = stageEl.closest('.pdp-view'), handle = view.dataset.productHandle;
  const img = $('#stageImg'), form = $('#pdpForm'), addBtn = $('#addBtn');
  let qty = 1; const price = addBtn ? +addBtn.dataset.price : 0, max = addBtn ? Math.max(1, +addBtn.dataset.max || 99) : 1;
  const mb = $('#mobileBuy');
  const update = () => { if (!addBtn) return; $('#addPrice').textContent = money(price * qty); const mp = $('#mbPrice'); if (mp) mp.textContent = money(price * qty); $('#qtyOut').textContent = qty; $('#qtyInput').value = qty; };
  let viewer = null;
  const setView = (id, btn) => {
    $$('.thumb', view).forEach(t => t.setAttribute('aria-pressed', t === btn));
    const is3d = id === '3d';
    img.classList.toggle('on', !is3d); $('.stage__tools', view).style.visibility = is3d ? 'visible' : 'hidden';
    if (viewer) viewer.show(is3d);
    if (!is3d) { img.src = btn.dataset.src; img.alt = id === 'flat' ? `${stageEl.dataset.title} artwork` : `${stageEl.dataset.title} sticker`; img.classList.toggle('contain', id === 'flat'); }
  };
  view.addEventListener('click', e => {
    const q = e.target.closest('[data-q]'); if (q) { qty = clamp(qty + +q.dataset.q, 1, Math.min(99, max)); update(); return; }
    const th = e.target.closest('.thumb'); if (th) setView(th.dataset.view, th);
  });
  if (form) {
    form.addEventListener('submit', async e => { e.preventDefault(); const v = form.querySelector('[name="id"]').value; await addToCart(v, qty); qty = 1; update(); });
    if (mb) {
      mb.innerHTML = `<b>${esc(stageEl.dataset.title)}</b><button class="btn btn--leaf btn--small" id="mbAdd" type="button">Add · <span id="mbPrice"></span></button>`;
      $('#mbAdd').addEventListener('click', () => form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })));
      new IntersectionObserver(([en]) => mb.classList.toggle('on', !en.isIntersecting && innerWidth <= 900), { threshold: 0 }).observe(addBtn);
    }
    update();
  }
  refreshSetCards();
  if (!reduce) { gsap.from('#pdpTitle', { y: 60, rotate: 2, duration: 1, ease: 'back.out(1.5)', delay: .1 }); gsap.from('.pdp__right > *:not(#pdpTitle)', { y: 30, duration: .8, stagger: .05, ease: 'power3.out', delay: .15 }); }
  // Recently viewed
  if (MP.recentlyViewed) {
    const seen = store.get('mp-recent', []).filter(h => h !== handle);
    const list = seen.map(h => byHandle[h]).filter(Boolean).slice(0, 4);
    if (list.length && $('#recentWrap')) { $('#recentCards').innerHTML = list.map(cardHTML).join(''); bindCards($('#recentCards')); $('#recentWrap').hidden = false; }
    store.set('mp-recent', [handle, ...seen].slice(0, 8));
  }
  // 3D viewer
  if (stageEl.dataset['3d'] === 'true') {
    const im = await loadImg(stageEl.dataset.art);
    viewer = createViewer(); viewer.mount(stageEl, im || fallbackTex(), stageEl.dataset.title);
    const pb = $('#peelBtn'); if (pb) pb.addEventListener('click', () => viewer.togglePeel());
  }
}

// Sticker spotlight sections (optional home section)
$$('[data-spotlight]').forEach(async el => {
  const im = await loadImg(el.dataset.art); const v = createViewer();
  const flat = $('.stage__img', el); if (flat) flat.classList.remove('on');
  v.mount(el, im || fallbackTex(), el.dataset.title);
});

// === COLLECTION PAGE ===
(function collectionPage() {
  const sel = $('#sortSel[data-sort]'); if (!sel) return;
  fancySelect(sel);
  sel.addEventListener('change', () => { const u = new URL(location.href); u.searchParams.set('sort_by', sel.value); u.searchParams.delete('page'); navigate(u.toString()); });
})();

// === DRAWER: cart + story / help / contact panels ===
let drawerMode = null, lastFocus = null;
function openDrawer(mode) {
  const d = $('#drawer'); if (!d) return;
  const wasOpen = !!drawerMode;
  drawerMode = mode; if (!wasOpen) lastFocus = document.activeElement;
  if (mode === 'bag') renderBag(); else renderInfo(mode);
  if (!wasOpen) { d.classList.add('on'); d.setAttribute('aria-hidden', 'false'); $('#scrim').classList.add('on'); lockScroll(true); setTimeout(() => $('#drawerClose').focus(), 60); }
}
function closeDrawer() { if (!drawerMode) return; const d = $('#drawer'); d.classList.remove('on'); d.setAttribute('aria-hidden', 'true'); $('#scrim').classList.remove('on'); lockScroll(false); drawerMode = null; lastFocus && lastFocus.focus && lastFocus.focus(); }
$('#drawerClose') && $('#drawerClose').addEventListener('click', closeDrawer);
$('#scrim') && $('#scrim').addEventListener('click', () => { closeDrawer(); closeSearch(); });
document.addEventListener('click', e => {
  const cartBtn = e.target.closest('#bagBtn, [data-open-cart]'); if (cartBtn) { e.preventDefault(); closeMenu(); openDrawer('bag'); return; }
  const op = e.target.closest('[data-open]'); if (op && !op.closest('.thumbs')) { e.preventDefault(); closeMenu(); openDrawer(op.dataset.open); }
});
function nudgeHTML() {
  if (!BUNDLE.enabled || !BUNDLE.nudge) return '';
  const ni = nudgeInfo(); if (!ni.n) return ''; const sz = BUNDLE.size;
  const dots = Array.from({ length: sz }, (_, i) => `<i class="${i < ni.filled ? 'on' : ''}"></i>`).join('');
  if (ni.rem === 0) return `<div class="nudge done"><div class="nudge__row"><b>${ni.sets} set${ni.sets > 1 ? 's' : ''} of ${sz}: you're saving ${money(ni.saving)}</b><span class="dots">${'<i class="on"></i>'.repeat(sz)}</span></div><p>Add ${sz} more stickers and that set is ${money(BUNDLE.price)} too.</p></div>`;
  return `<div class="nudge"><div class="nudge__row"><b>Add ${ni.need} more sticker${ni.need > 1 ? 's' : ''} to make it ${sz} for ${money(BUNDLE.price)}</b><span class="dots" aria-label="${ni.filled} of ${sz}">${dots}</span></div><p>Any mix works. Every set of ${sz} comes to ${money(BUNDLE.price)} instead of ${money(UNIT * sz)}${ni.sets ? `, and you're already saving ${money(ni.saving)}` : ''}.</p></div>`;
}
function goalHTML() {
  const g = MP.cart && MP.cart.goal; if (!g || !g.amount) return '';
  const left = Math.max(0, g.amount - cart.total_price), pct = clamp(cart.total_price / g.amount) * 100;
  const text = left > 0 ? esc(g.text || '').replace('[remaining]', `<b>${money(left)}</b>`) : esc(g.done || '');
  return `<div class="opt-goal"><p>${text}</p><div class="opt-goal__bar"><i style="width:${pct.toFixed(1)}%"></i></div></div>`;
}
function lineHTML(it) {
  const p = byHandle[it.handle] || {};
  const tint = p.tint || C.cream; const meta = [p.size, 'Matte'].filter(Boolean).join(' · ');
  const discounted = it.original_line_price !== it.final_line_price;
  return `<div class="line" data-key="${esc(it.key)}" data-qty="${it.quantity}"><a class="line__img" href="${esc(it.url)}" style="--tint:${esc(tint)}">${it.image ? `<img src="${esc(it.image)}" alt="">` : ''}</a><div style="min-width:0"><a class="line__name" href="${esc(it.url)}">${esc(it.product_title)}</a><div class="line__meta">${esc(meta)}</div><div class="qty" role="group" aria-label="Quantity for ${esc(it.product_title)}"><button type="button" data-lq="-1" aria-label="One fewer">−</button><output>${it.quantity}</output><button type="button" data-lq="1" aria-label="One more">+</button></div></div><div class="line__right">${money(discounted ? it.original_line_price : it.final_line_price)}<button class="text-btn" type="button" data-remove>Remove</button></div></div>`;
}
function discountRows() {
  const rows = new Map();
  cart.items.forEach(it => (it.line_level_discount_allocations || []).forEach(a => { const t = a.discount_application.title; rows.set(t, (rows.get(t) || 0) + a.amount); }));
  (cart.cart_level_discount_applications || []).forEach(a => rows.set(a.title, (rows.get(a.title) || 0) + a.total_allocated_amount));
  return [...rows].filter(([, v]) => v > 0).map(([t, v]) => {
    const isReward = reward && t.toUpperCase() === reward.code;
    return `<div class="sum save"><span>${isReward ? `Welcome reward (${esc(reward.code)})` : esc(t)}</span><span>−${money(v)}</span></div>`;
  }).join('');
}
function summaryHTML() {
  const sub = cart.items.reduce((a, it) => a + it.original_line_price, 0);
  return `<div class="sum"><span>Subtotal (${cart.item_count} sticker${cart.item_count === 1 ? '' : 's'})</span><span>${money(sub)}</span></div>
    ${discountRows()}
    <div class="sum"><span>Delivery</span><span>Free islandwide</span></div>
    <div class="sum total"><span>Total</span><span>${money(cart.total_price)}</span></div>`;
}
function renderBag() {
  $('#drawerTitle').textContent = 'Your cart';
  const body = $('#drawerBody'), foot = $('#drawerFoot');
  if (!cart.items.length) {
    body.innerHTML = `<div class="empty"><img src="${esc(MP.assets && MP.assets.cartNap)}" alt="Mooki and Tooki napping in shopping carts" style="width:min(300px,80%)"><h3 style="font-size:1.6rem">Your cart is napping.</h3><p class="lede" style="text-align:center">Nothing in here yet.${BUNDLE.enabled ? ` Any ${BUNDLE.size} stickers are just ${money(BUNDLE.price)}.` : ''}</p><a class="btn btn--sun" href="${esc(R.shop)}">Browse stickers</a></div>`;
    foot.innerHTML = ''; return;
  }
  const inBag = new Set(cart.items.map(l => l.handle));
  const avail = STICKERS.filter(s => s.available);
  const sugg = avail.filter(s => !inBag.has(s.handle)).concat(avail.filter(s => inBag.has(s.handle))).slice(0, 4);
  const codes = (cart.discount_codes || []);
  const rc = reward ? codes.find(c => c.code.toUpperCase() === reward.code) : null;
  const rewardRow = reward ? `<div class="reward-row"><div>Welcome reward: ${esc(reward.label)}<small>${rc && rc.applicable ? `Code ${esc(reward.code)} applied` : rc ? (reward.code === 'FREESTICKER' ? 'Add one more sticker to use your free sticker.' : `Code ${esc(reward.code)} will apply at checkout`) : `Code ${esc(reward.code)} applies at checkout`}</small></div>${flowerSVG()}</div>` : '';
  body.innerHTML = goalHTML() + nudgeHTML() + cart.items.map(lineHTML).join('') +
    (MP.cart && MP.cart.suggestions && sugg.length ? `<div class="suggest"><h3>${BUNDLE.enabled && nudgeInfo().rem ? 'Finish your set' : 'Start another set'}</h3><div class="mini-adds">${sugg.map(s => `<button class="mini-add" type="button" data-add="${s.id}" aria-label="Add ${esc(s.name)} to cart"><img src="${esc(s.art)}" alt="">${esc(s.name)} ${plusSVG}</button>`).join('')}</div></div>` : '') +
    rewardRow +
    (MP.cart && MP.cart.giftNote ? `<label class="opt-note" for="cartNote">Gift note (optional)<textarea class="field" id="cartNote" maxlength="300" placeholder="We'll pop it in the envelope">${esc(cart.note || '')}</textarea></label>` : '');
  foot.innerHTML = summaryHTML() + `<button class="btn btn--leaf" id="toCheckout" type="button" style="margin-top:6px">Checkout · ${money(cart.total_price)}</button><p class="note" style="margin:8px 0 0;text-align:center">Delivery details and payment on the next step.</p>`;
  $('#toCheckout').addEventListener('click', async (e) => {
    const b = e.currentTarget; b.disabled = true; b.textContent = 'Heading to checkout…';
    const n = $('#cartNote'); if (n && n.value !== (cart.note || '')) { try { await api(R.cartUpdate + '.js', { note: n.value }); } catch (x) { } }
    location.href = '/checkout';
  });
}
$('#drawerBody') && $('#drawerBody').addEventListener('click', e => {
  const line = e.target.closest('.line'); if (!line) { if (e.target.closest('a')) closeDrawer(); return; }
  const key = line.dataset.key, q = +line.dataset.qty;
  const b = e.target.closest('[data-lq]'); if (b) { changeLine(key, Math.max(0, q + +b.dataset.lq)); return; }
  if (e.target.closest('[data-remove]')) { changeLine(key, 0); return; }
  if (e.target.closest('a')) closeDrawer();
});
function renderInfo(mode) {
  const tpl = $(`#panel-${mode}`); const body = $('#drawerBody'), foot = $('#drawerFoot'); foot.innerHTML = '';
  if (!tpl) { body.innerHTML = ''; return; }
  $('#drawerTitle').textContent = tpl.dataset.title || '';
  body.innerHTML = ''; body.appendChild(tpl.content.cloneNode(true));
  if (mode === 'contact') bindContact($('#ctForm', body));
}
function bindContact(f) {
  if (!f) return;
  const sel = $('select', f); if (sel) fancySelect(sel);
  if ($('[data-posted]', f)) { const out = $('#ctMsgOut', f); out.style.color = 'var(--leaf-deep)'; out.textContent = 'Thanks! Your message is on its way. We’ll reply by email.'; }
  f.addEventListener('submit', e => {
    let bad = null; const name = f.querySelector('[name="contact[name]"]'), em = f.querySelector('[name="contact[email]"]'), msg = f.querySelector('[name="contact[body]"]');
    $$('.field', f).forEach(x => x.removeAttribute('aria-invalid'));
    if (name.value.trim().length < 2) bad = bad || name;
    if (!EMAIL_RE.test(em.value.trim())) bad = bad || em;
    if (msg.value.trim().length < 10) bad = bad || msg;
    if (!bad) return; // let Shopify send it
    e.preventDefault(); const out = $('#ctMsgOut', f);
    bad.setAttribute('aria-invalid', 'true'); bad.focus(); out.style.color = 'var(--warn)';
    out.textContent = bad === em ? 'That email looks off. Try you@example.com.' : bad === msg ? 'Tell us a little more (10 characters or so).' : 'Add your name so we know who to reply to.';
  });
}
// Re-open the contact panel after Shopify sends the message (or returns errors)
(() => { const tpl = $('#panel-contact'); if (!tpl) return; const out = tpl.content.querySelector('#ctMsgOut'); if (tpl.content.querySelector('[data-posted]') || (out && out.textContent.trim())) setTimeout(() => openDrawer('contact'), 400); })();

// === EMAIL SIGN-UPS (sticker club, coming soon, drop and restock alerts) ===
async function postCustomerForm(form) {
  const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
  if (/\/challenge/.test(res.url)) return 'challenge';
  return res.ok ? 'ok' : 'error';
}
const SIGNUP_MSG = {
  club: 'You’re in the sticker club!',
  soon: 'You’re on the list! We’ll email you when Mooki & Tooki drop.',
  drop: 'Done! We’ll remind you when it drops.',
  restock: 'Got it. We’ll email you when it’s back.'
};
$$('form[data-mp-signup]').forEach(form => {
  const kind = form.dataset.mpSignup;
  const msg = form.nextElementSibling && form.nextElementSibling.classList.contains('form-msg') ? form.nextElementSibling : $('.form-msg', form.parentNode);
  const inp = form.querySelector('[type="email"]');
  const say = (t, ok) => { if (!msg) return toast(esc(t)); msg.classList.toggle('err', !ok); msg.style.color = ok ? 'var(--leaf-deep)' : 'var(--warn)'; msg.textContent = t; };
  if ($('[data-posted]', form)) say(SIGNUP_MSG[kind] || SIGNUP_MSG.club, true);
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const em = inp.value.trim().toLowerCase();
    if (!EMAIL_RE.test(em)) { inp.setAttribute('aria-invalid', 'true'); say('That email looks off. Try you@example.com.', false); inp.focus(); return; }
    inp.removeAttribute('aria-invalid');
    const btn = form.querySelector('[type="submit"]'); if (btn) btn.disabled = true;
    try {
      const r = await postCustomerForm(form);
      if (r === 'challenge') { form.submit(); return; }
      if (r === 'error') throw 0;
      say(SIGNUP_MSG[kind] || SIGNUP_MSG.club, true); if (kind !== 'soon') inp.value = '';
    } catch (x) { say('That didn’t go through. Please try again.', false); }
    finally { if (btn) btn.disabled = false; }
  });
});
if (/customer_posted=true/.test(location.search)) setTimeout(() => toast('You’re in the sticker club!'), 900);

// Countdown (optional drop section). Dates are Singapore time.
$$('[data-countdown]').forEach(el => {
  const raw = (el.dataset.countdown || '').trim().replace(' ', 'T');
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : raw + (raw.length <= 10 ? 'T00:00' : '') + ':00+08:00');
  if (isNaN(t)) { el.hidden = true; return; }
  const u = k => $(`[data-u="${k}"]`, el);
  const tick = () => {
    let s = Math.max(0, Math.floor((t - Date.now()) / 1000));
    if (s === 0) { el.innerHTML = `<p class="opt-count__done">${esc(el.dataset.done)}</p>`; return; }
    const d = Math.floor(s / 86400); s -= d * 86400; const h = Math.floor(s / 3600); s -= h * 3600; const m = Math.floor(s / 60); s -= m * 60;
    u('d').textContent = String(d).padStart(2, '0'); u('h').textContent = String(h).padStart(2, '0'); u('m').textContent = String(m).padStart(2, '0'); u('s').textContent = String(s).padStart(2, '0');
    setTimeout(tick, 1000);
  };
  tick();
});

// === WELCOME WHEEL ===
const Wheel = (() => {
  const el = $('#wheel'), cfgEl = $('#wheelConfig');
  if (!el || !cfgEl) return { maybeAuto() { }, syncTab() { }, hideTab() { } };
  let CFG; try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return { maybeAuto() { }, syncTab() { }, hideTab() { } }; }
  const prizes = (CFG.prizes || []).filter(p => p.code && p.chance > 0);
  if (!prizes.length) return { maybeAuto() { }, syncTab() { }, hideTab() { } };
  // Spread each prize's wedges around the wheel
  const left = prizes.map(p => Math.max(1, p.slices | 0)); const SLICES = []; let prev = -1;
  while (left.some(n => n > 0)) { let best = -1; left.forEach((n, i) => { if (n > 0 && i !== prev && (best < 0 || n > left[best])) best = i; }); if (best < 0) best = left.findIndex(n => n > 0); SLICES.push(best); left[best]--; prev = best; }
  const FILLS = [C.sun, C.blush, C.sage, C.mari, C.cream, C.leaf, C.blush, C.sage, C.sun, C.mari, C.cream, C.leaf, C.sage, C.blush, C.sun, C.mari];
  const NS = SLICES.length, DEG = 360 / NS;
  const body = $('#wheelBody'), stageEl = $('#wheelStage'), tab = $('#wheelTab');
  let rot = 0, state = 'form', email = '', won = null, timer = null, built = false, tabWanted = false;
  function build() {
    if (built) return; built = true;
    const Rr = 128, cx = 215, cy = 200; let slices = '', labels = '';
    SLICES.forEach((pi, i) => {
      const p = prizes[pi], fill = FILLS[i % FILLS.length], ink = fill === C.leaf ? C.cream : '#2B2219';
      const a0 = i * DEG * Math.PI / 180, a1 = (i + 1) * DEG * Math.PI / 180;
      slices += `<path d="M0 0 L${(Rr * Math.sin(a0)).toFixed(1)} ${(-Rr * Math.cos(a0)).toFixed(1)} A${Rr} ${Rr} 0 0 1 ${(Rr * Math.sin(a1)).toFixed(1)} ${(-Rr * Math.cos(a1)).toFixed(1)} Z" fill="${fill}" stroke="#2B2219" stroke-width="3" stroke-linejoin="round"/>`;
      const big = String(p.a).length > 3 ? 21 : 27;
      labels += `<g transform="rotate(${i * DEG + DEG / 2})" fill="${ink}" text-anchor="middle"><text data-r="94" y="-87" font-family="Fraunces, Georgia, serif" font-weight="900" font-size="${big}">${esc(p.a)}</text><text data-r="72" y="-69" font-family="Nunito, sans-serif" font-weight="900" font-size="10" letter-spacing="1">${esc(p.b)}</text></g>`;
    });
    let bulbs = ''; for (let k = 0; k < 16; k++) { const a = (k * 22.5 + 11.25) * Math.PI / 180; bulbs += `<circle cx="${(cx + 139 * Math.sin(a)).toFixed(1)}" cy="${(cy - 139 * Math.cos(a)).toFixed(1)}" r="5" fill="${k % 2 ? '#FFFDF8' : '#F4C542'}"/>`; }
    stageEl.innerHTML = `<svg viewBox="0 0 430 560" role="img" aria-label="Prize wheel held up by Mooki and Tooki. Prizes: ${esc(prizes.map(p => (p.a + ' ' + p.b).trim()).join(', '))}.">
      <ellipse cx="306" cy="514" rx="54" ry="8" fill="#2B2219" opacity=".14"/><ellipse cx="128" cy="524" rx="50" ry="8" fill="#2B2219" opacity=".14"/>
      <circle cx="${cx}" cy="${cy}" r="150" fill="#FFFDF8" stroke="#2B2219" stroke-width="5"/>
      <g class="wheel__spin" id="wheelSpin"><g transform="translate(${cx} ${cy})">${slices}${labels}</g></g>
      <circle cx="${cx}" cy="${cy}" r="128" fill="none" stroke="#2B2219" stroke-width="4"/>
      <g stroke="#2B2219" stroke-width="2.5">${bulbs}</g>
      <circle cx="${cx}" cy="${cy}" r="25" fill="#FFFDF8" stroke="#2B2219" stroke-width="4"/>
      <g transform="translate(${cx} ${cy}) scale(1.55)"><use href="#i-flower"/><use href="#i-flower-dot"/></g>
      <path d="M215 88 L199 46 C205 35 225 35 231 46 Z" fill="#F28C38" stroke="#2B2219" stroke-width="4" stroke-linejoin="round"/>
      <circle cx="215" cy="45" r="5" fill="#FFFDF8" stroke="#2B2219" stroke-width="3"/>
    </svg><img class="char char--monkey" src="${esc(CFG.art.monkey)}" alt=""><img class="char char--pup" src="${esc(CFG.art.dog)}" alt="">`;
  }
  function fitLabels() {
    $$('#wheelSpin text').forEach(t => {
      const r = +t.dataset.r, maxW = 2 * r * Math.tan(Math.PI / NS) - 12;
      let fs = +t.getAttribute('font-size'), w = 0;
      try { w = t.getComputedTextLength(); } catch (e) { return; }
      if (w > maxW && w > 0) t.setAttribute('font-size', (fs * maxW / w).toFixed(1));
    });
  }
  function pickSlice() {
    const total = prizes.reduce((a, p) => a + p.chance, 0);
    let r = Math.random() * total, pi = 0;
    for (let i = 0; i < prizes.length; i++) { r -= prizes[i].chance; if (r < 0) { pi = i; break; } }
    const idxs = SLICES.map((s, i) => s === pi ? i : -1).filter(i => i >= 0);
    return { pi, slice: idxs[Math.floor(Math.random() * idxs.length)] };
  }
  const copy = CFG.copy || {};
  function renderBody() {
    if (state === 'won' && won) {
      const P = prizes[won.pi];
      body.innerHTML = `<p class="eyebrow">${esc(copy.wonEyebrow)}</p><h2 id="wheelTitle">You won ${esc(P.title)}!</h2><p>${esc(P.detail)}</p>
        <div class="wheel__code"><b>${esc(P.code)}</b><button class="btn btn--small" type="button" id="wCopy">Copy code</button></div>
        <p class="fine">Saved for ${esc(email)}. ${esc(copy.wonFine)}</p>
        <button class="btn btn--leaf" type="button" id="wShop" style="align-self:flex-start">Start shopping</button>`;
      $('#wCopy').onclick = (e) => { const b = e.currentTarget; try { navigator.clipboard.writeText(P.code).then(() => b.textContent = 'Copied', () => b.textContent = 'Copied'); } catch (x) { b.textContent = 'Copied'; } };
      $('#wShop').onclick = () => { close(); if (TPL !== 'collection') navigate(R.shop); };
      return;
    }
    const spinning = state === 'spinning';
    body.innerHTML = `<p class="eyebrow">${esc(copy.eyebrow)}</p><h2 id="wheelTitle">${esc(copy.heading)}</h2>
      <p>${esc(copy.text)}</p>
      <form id="wForm" novalidate style="display:grid;gap:8px;margin-top:4px">
        <label for="wEmail" style="font-weight:900;font-size:.78rem;letter-spacing:.07em;text-transform:uppercase">Your email</label>
        <div class="wheel__row"><input class="field" id="wEmail" type="email" autocomplete="email" placeholder="you@example.com" value="${esc(email)}" ${spinning ? 'disabled' : ''}><button class="btn btn--ink" type="submit" ${spinning ? 'disabled' : ''}>${spinning ? 'Spinning…' : esc(copy.button)}</button></div>
        <p class="err-text" id="wErr" role="alert" hidden style="margin:2px 0 0 14px"></p>
      </form>
      <p class="fine">${esc(copy.fine)}</p>
      <button class="text-btn" type="button" data-wclose style="align-self:flex-start;padding:8px 0;font-size:.95rem">${esc(copy.decline)}</button>`;
    $('#wForm').onsubmit = submit;
    $$('[data-wclose]', body).forEach(b => b.onclick = close);
  }
  function submit(e) {
    e.preventDefault(); if (state !== 'form') return;
    const em = $('#wEmail').value.trim().toLowerCase(); email = em;
    const err = (m) => { const x = $('#wErr'); x.hidden = false; x.textContent = m; $('#wEmail').setAttribute('aria-invalid', 'true'); $('#wEmail').focus(); };
    if (!EMAIL_RE.test(em)) return err('That email looks off. Try you@example.com.');
    if (store.get('mp-wheel-emails', []).includes(em)) return err('This email has already had its welcome spin.');
    const pick = pickSlice();
    const jitter = (Math.random() - 0.5) * DEG * 0.6;
    const target = ((360 - (pick.slice * DEG + DEG / 2) + jitter) % 360 + 360) % 360;
    rot = rot - (rot % 360) + 360 * 6 + target;
    state = 'spinning'; renderBody(); el.classList.add('is-spinning');
    const spin = $('#wheelSpin');
    spin.style.transition = reduce ? 'none' : 'transform 4.6s cubic-bezier(.12,.75,.1,1)';
    spin.style.transform = `rotate(${rot}deg)`;
    // Join the sticker club in the background, tagged with the prize
    const f = $('#wheelSignup');
    if (f) { f.querySelector('[name="contact[email]"]').value = em; const tg = f.querySelector('[name="contact[tags]"]'); tg.value = tg.value.replace(/,wheel-[A-Z0-9]+$/, '') + ',wheel-' + prizes[pick.pi].code; postCustomerForm(f).catch(() => { }); }
    clearTimeout(timer);
    timer = setTimeout(() => finish(pick), reduce ? 50 : 4800);
  }
  function finish(pick) {
    won = pick; state = 'won'; el.classList.remove('is-spinning'); el.classList.add('is-won');
    const P = prizes[pick.pi];
    reward = { code: P.code, label: P.title, email }; store.set('mp-reward', reward);
    store.set('mp-wheel-emails', [...store.get('mp-wheel-emails', []), email]);
    store.set('mp-wheel-done', true); hideTab();
    applyCode(P.code);
    renderBody(); if (drawerMode === 'bag') renderBag();
    setTimeout(() => el.classList.remove('is-won'), 1200);
  }
  function open() {
    build(); if (state !== 'won') state = 'form';
    renderBody(); el.hidden = false; syncTab(); lockScroll(true);
    requestAnimationFrame(fitLabels);
    if (!reduce) { gsap.fromTo('.wheel__card', { y: 60, scale: .94, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: .6, ease: 'back.out(1.5)' }); gsap.fromTo('.wheel__scrim', { opacity: 0 }, { opacity: 1, duration: .4 }); }
    setTimeout(() => { const i = $('#wEmail'); if (i && finePointer) i.focus(); }, 300);
  }
  function close() {
    if (el.hidden) return;
    if (state === 'spinning') return; // let the spin finish
    el.hidden = true; lockScroll(false);
    store.set('mp-wheel-seen', true);
    tabWanted = state !== 'won' && CFG.showTab && !MP.customer; syncTab();
  }
  const phone = matchMedia('(max-width: 760px)');
  const onPage = () => CFG.pages === 'all' || TPL === 'index' || (CFG.pages === 'shop' && TPL === 'collection');
  function syncTab() {
    if (!tab) return;
    let ok = tabWanted && el.hidden && onPage();
    if (ok && phone.matches && TPL === 'index') { const f = document.getElementById('featured'); ok = !!f && f.getBoundingClientRect().top < innerHeight; }
    tab.hidden = !ok;
  }
  addEventListener('scroll', () => { if (tabWanted) syncTab(); }, { passive: true });
  function hideTab() { tabWanted = false; syncTab(); }
  $$('[data-wclose]', el).forEach(b => b.addEventListener('click', close));
  tab && tab.addEventListener('click', () => open());
  addEventListener('keydown', e => { if (e.key === 'Escape' && !el.hidden) close(); });
  function eligible() { return !MP.customer && !store.get('mp-wheel-done', false); }
  function maybeAuto() {
    if (!eligible() || !onPage()) return;
    if (store.get('mp-wheel-seen', false)) { tabWanted = CFG.showTab; syncTab(); return; }
    setTimeout(() => { if (eligible() && el.hidden && !drawerMode && !menuOpen) open(); else if (eligible()) { tabWanted = CFG.showTab; syncTab(); } }, CFG.delayMs || 4000);
  }
  return { open, close, hideTab, maybeAuto, syncTab };
})();

// === MENU + SEARCH ===
{ const m = $('#menu'); if (m && m.parentNode !== document.body) document.body.appendChild(m); } // keep the menu above everything
function openMenu() { const m = $('#menu'); if (!m) return; menuOpen = true; m.classList.add('on'); m.setAttribute('aria-hidden', 'false'); $('#menuBtn').setAttribute('aria-expanded', 'true'); lockScroll(true); if (!reduce) gsap.from('#menuNav > *', { y: 60, rotate: 3, duration: .8, stagger: .05, ease: 'back.out(1.5)', delay: .15 }); setTimeout(() => $('#menuClose').focus(), 80); }
function closeMenu() { const m = $('#menu'); if (!m || !m.classList.contains('on')) return; menuOpen = false; m.classList.remove('on'); m.setAttribute('aria-hidden', 'true'); $('#menuBtn').setAttribute('aria-expanded', 'false'); lockScroll(false); }
$('#menuBtn') && $('#menuBtn').addEventListener('click', openMenu);
$('#menuClose') && $('#menuClose').addEventListener('click', closeMenu);
$$('#menuNav a').forEach(a => a.addEventListener('click', closeMenu));
$$('#menuNav [data-peek]').forEach(a => a.addEventListener('pointerenter', () => { const im = $('#menuPeek'); if (!a.dataset.peek) return; im.src = a.dataset.peek; gsap.fromTo(im, { rotate: -10, scale: .85 }, { rotate: 0, scale: 1, duration: .6, ease: 'back.out(2)' }); }));
let searchOpen = false;
function openSearch() { const s = $('#search'); if (!s) return; searchOpen = true; s.classList.add('on'); s.setAttribute('aria-hidden', 'false'); $('#scrim').classList.add('on'); lockScroll(true); doSearch(''); setTimeout(() => $('#searchInput').focus(), 100); }
function closeSearch() { if (!searchOpen) return; searchOpen = false; const s = $('#search'); s.classList.remove('on'); s.setAttribute('aria-hidden', 'true'); if (!drawerMode) $('#scrim').classList.remove('on'); lockScroll(false); }
function doSearch(q) {
  q = q.trim().toLowerCase(); const res = STICKERS.filter(p => !q || [p.name, p.sub, p.tagline, p.keys].join(' ').toLowerCase().includes(q));
  $('#searchResults').innerHTML = res.length ? res.map(p => `<a class="sr" href="${esc(p.url)}"><img src="${esc(p.art)}" alt=""><span><b>${esc(p.name)}</b><small>${p.sub ? esc(p.sub) + ' · ' : ''}${money(p.price)}</small></span></a>`).join('') : `<p style="font-weight:700;margin:0">No stickers match “${esc(q)}”. Try fairy, clown or tired.</p>`;
}
$('#searchBtn') && $('#searchBtn').addEventListener('click', openSearch);
$('#searchClose') && $('#searchClose').addEventListener('click', closeSearch);
$('#searchInput') && $('#searchInput').addEventListener('input', e => doSearch(e.target.value));
$('#search form') && $('#search form').addEventListener('submit', e => { const a = $('#searchResults a'); if (a) { e.preventDefault(); navigate(a.href); } });
addEventListener('keydown', e => { if (e.key === 'Escape') { closeMenu(); closeSearch(); if (drawerMode) closeDrawer(); } });

// === HEADER STATE ===
const header = $('#header');
const onHeader = () => header && header.classList.toggle('is-scrolled', TPL === 'index' ? scrollY > (Hero ? Hero.end || innerHeight * 4 : 40) : scrollY > 40);
addEventListener('scroll', onHeader, { passive: true });

// === PAGE TRANSITIONS: quick fade between pages ===
const main = $('#MainContent');
function navigate(url) {
  const u = new URL(url, location.href);
  if (u.origin !== location.origin) { location.href = u.href; return; }
  if (u.pathname === location.pathname && u.search === location.search && u.hash) { const t = document.getElementById(u.hash.slice(1)); if (t) { closeMenu(); scrollTo(t, { offset: -90, duration: 1.2 }); history.replaceState(null, '', u.hash); return; } }
  if (reduce || !main) { location.href = u.href; return; }
  main.classList.add('is-leaving'); setTimeout(() => { location.href = u.href; }, 240);
}
document.addEventListener('click', e => {
  const a = e.target.closest('a[href]'); if (!a || e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0 || a.target === '_blank' || a.hasAttribute('download')) return;
  const href = a.getAttribute('href'); if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript')) return;
  const u = new URL(href, location.href); if (u.origin !== location.origin) return;
  if (/^\/(checkout|account|cart\/|discount)/.test(u.pathname) && !/^\/account$/.test(u.pathname)) return;
  e.preventDefault(); closeDrawer(); closeSearch(); navigate(u.href);
});
addEventListener('pageshow', () => main && main.classList.remove('is-leaving'));

// === SCROLL REVEALS (transform only) ===
function revealAnimations() {
  if (reduce) return;
  $$('.reveal').forEach(h => gsap.from(h, { y: 50, rotate: 1.5, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: h, start: 'top 88%' } }));
  $$('.cards--strip, .cards--grid').forEach(g => { const cs = $$(':scope > .card', g); if (cs.length) gsap.from(cs, { y: 80, rotate: (i) => (i % 2 ? 3 : -3), duration: 1.05, stagger: .07, ease: 'back.out(1.3)', scrollTrigger: { trigger: g, start: 'top 92%' } }); });
  if ($('.bundle__math')) gsap.from('.bundle__math > *', { scale: .4, rotate: -12, duration: .8, stagger: .07, ease: 'back.out(2)', scrollTrigger: { trigger: '.bundle__math', start: 'top 85%' } });
  $$('.voices__grid').forEach(g => gsap.from($$('.voice', g), { y: 80, rotate: (i) => [-6, 4, -3][i % 3], duration: 1, stagger: .1, ease: 'back.out(1.4)', scrollTrigger: { trigger: g, start: 'top 90%' } }));
  $$('[data-parallax]').forEach(im => gsap.fromTo(im, { yPercent: -10 }, { yPercent: 0, ease: 'none', scrollTrigger: { trigger: im.parentNode, scrub: true, start: 'top bottom', end: 'bottom top' } }));
  if ($('.wordmark__logo')) { gsap.from('.wordmark__logo', { yPercent: 40, scaleY: .7, transformOrigin: '50% 100%', ease: 'back.out(1.5)', duration: 1.1, scrollTrigger: { trigger: '.wordmark', start: 'top 85%' } }); gsap.from('.wordmark .drag', { scale: 0, rotate: -40, duration: .8, stagger: .08, ease: 'back.out(2.2)', scrollTrigger: { trigger: '.wordmark', start: 'top 75%' } }); }
  $$('.story__duo').forEach(d => gsap.from($$('img', d), { y: 40, rotate: (i) => i ? 10 : -10, duration: .9, stagger: .12, ease: 'back.out(2)', scrollTrigger: { trigger: d, start: 'top 92%' } }));
  $$('.opt-ticket, .opt-count > div').forEach((t, i) => gsap.from(t, { y: 50, rotate: i % 2 ? 2 : -2, duration: .9, ease: 'back.out(1.5)', scrollTrigger: { trigger: t, start: 'top 92%' } }));
  if ($('.soon-tile')) gsap.from('.soon-tile', { y: 50, rotate: i => (i % 2 ? 4 : -4), duration: .8, stagger: .06, ease: 'back.out(1.6)' });
}

// === BOOT ===
(async function boot() {
  cartChanged();
  if (/#cart$/.test(location.hash)) setTimeout(() => openDrawer('bag'), 200);
  const L = $('#loader');
  if (Hero) {
    const pile = $('#loaderPile');
    const order = STICKERS.slice(0, 5).reverse();
    if (pile) pile.innerHTML = order.map(s => `<img src="${esc(s.art)}" alt="">`).join('');
    const shown = { v: 0 }; let loaded = 0; const total = STICKERS.length + 1;
    const tick = () => {
      if (!L) return; const pct = Math.round(loaded / total * 100);
      gsap.to(shown, { v: pct, duration: .4, ease: 'power1.out', onUpdate: () => { const p = $('#pct'); if (p) p.textContent = Math.round(shown.v); } });
      const k = Math.min(order.length, Math.ceil(loaded / total * order.length));
      $$('img', pile).forEach((im, i) => { if (i < k && !im.dataset.in) { im.dataset.in = 1; gsap.to(im, { opacity: 1, scale: 1 - (order.length - i) * 0.02, rotate: (i % 2 ? 8 : -8) * (order.length - i) / 2, duration: .6, ease: 'back.out(2)' }); } });
    };
    const fontP = (document.fonts && document.fonts.ready) ? Promise.race([document.fonts.ready, new Promise(r => setTimeout(r, 2500))]) : Promise.resolve();
    await Promise.all([...STICKERS.map(s => loadImg(s.art).then(im => { IMGS[s.handle] = im || fallbackTex(); loaded++; tick(); })), fontP.then(() => { loaded++; tick(); })]);
    Hero.mount(); Hero.showMeta(0);
  }
  productPage();
  revealAnimations();
  if (L) {
    await new Promise(res => setTimeout(res, reduce ? 0 : 450));
    if (reduce) L.remove();
    else { await new Promise(res => gsap.timeline({ onComplete: res }).to($$('#loaderPile img').length ? '#loaderPile img' : {}, { y: -40, opacity: 0, rotate: 20, stagger: .04, duration: $$('#loaderPile img').length ? .4 : 0, ease: 'power2.in' }).to(L, { yPercent: -100, duration: .9, ease: 'power4.inOut' }, '-=.1')); L.remove(); }
  }
  if (Hero) Hero.intro();
  ScrollTrigger.refresh();
  if (location.hash.length > 1) { const t = document.getElementById(location.hash.slice(1)); if (t) setTimeout(() => scrollTo(t, { offset: -90, duration: 1.2 }), 250); }
  onHeader();
  Wheel.maybeAuto();
})();
